import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  startTemplateToVideoJob,
  pollTemplateJob,
  TemplateToVideoRequest,
} from '../services/templateToVideo';
import { templates, Template } from '../data/templates';
import TemplatePickerModal from '../components/TemplatePickerModal';
import { uploadImageToStorage } from '../services/storage';
import { useGenerationProgression } from '../hooks/useGenerationProgression';
import GenerationProcessingModal from '../components/modals/GenerationProcessingModal';
import { API_URL } from '../config/apiConfig';

type GenerationStatus = 'idle' | 'generating' | 'completed' | 'failed';

export default function TemplateToVideoPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [selectedFaceImage, setSelectedFaceImage] = useState<File | null>(null);
  const [faceImagePreview, setFaceImagePreview] = useState<string | null>(null);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [status, setStatus] = useState<GenerationStatus>('idle');
  const [progress, setProgress] = useState(0);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const cancelSimulationRef = useRef<(() => void) | null>(null);
  
  // Use progression tracking hook
  const {
    progression,
    setStage,
    updateStageProgress,
    setError: setProgressionError,
    reset: resetProgression,
  } = useGenerationProgression({
    onComplete: () => {
      // Progression completed
    },
    // Only log progression-specific errors (not video generation errors)
    // Video generation errors are logged separately
    onError: (err) => {
      // Only log if it's a progression tracking error, not a video generation error
      if (err && !err.includes('Failed to fetch') && !err.includes('Generation failed')) {
        console.error('❌ [TemplateToVideo] Progression tracking error:', err);
      }
    },
  });

  // Read templateId from URL query params or router state on mount
  useEffect(() => {
    const templateIdFromQuery = searchParams.get('templateId');
    const templateIdFromState = (location.state as any)?.templateId;

    const templateId = templateIdFromQuery || templateIdFromState;

    if (templateId) {
      const template = templates.find((t) => t.id === templateId);
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [searchParams, location.state]);

  const handleFaceImageSelect = useCallback(async (file: File) => {
    if (file && file.type.startsWith('image/')) {
      setSelectedFaceImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setFaceImagePreview(previewUrl);
      };
      reader.readAsDataURL(file);
      
      // Track upload progression - wrap in try-catch to prevent errors from breaking upload
      try {
        setStage('uploading_image', 0, 'Uploading face image...');
      } catch (error) {
        console.error('❌ [TemplateToVideo] Progression tracking error (non-fatal):', error);
      }
      
      // Upload to Supabase Storage
      try {
        // Simulate upload progress
        try {
          updateStageProgress(50);
        } catch (error) {
          console.error('❌ [TemplateToVideo] Progression progress error (non-fatal):', error);
        }
        
        const uploadResult = await uploadImageToStorage(file);
        
        // Track storage stages
        try {
          setStage('storing_image_s3', 100, 'Face image stored in S3');
          setTimeout(() => {
            try {
              setStage('storing_image_db', 100, 'Face image record saved');
            } catch (error) {
              console.error('❌ [TemplateToVideo] Progression storage error (non-fatal):', error);
            }
          }, 300);
        } catch (error) {
          console.error('❌ [TemplateToVideo] Progression tracking error (non-fatal):', error);
        }
        
        // Use uploaded URL instead of data URL
        setFaceImagePreview(uploadResult.url);
        
        // Store in sessionStorage
        sessionStorage.setItem('uploaded_face_image_url', uploadResult.url);
        sessionStorage.setItem('uploaded_face_image_path', uploadResult.path);
        sessionStorage.setItem('uploaded_face_image_asset_id', uploadResult.asset.id);
      } catch (error) {
        console.error('❌ [TemplateToVideo] Upload failed:', error);
        console.error('❌ [TemplateToVideo] Error details:', error instanceof Error ? error.message : String(error));
        setProgressionError(error instanceof Error ? error.message : 'Upload failed');
        // Continue with local preview even if upload fails
      }
    } else {
      console.warn('⚠️ [TemplateToVideo] Invalid file type:', file.type);
    }
  }, [setStage, updateStageProgress, setProgressionError]);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFaceImageSelect(file);
    } else {
      console.warn('⚠️ [TemplateToVideo] No file selected in input');
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleFaceImageSelect(file);
    } else {
      console.warn('⚠️ [TemplateToVideo] No file in drop event');
    }
  };

  const handleTemplateSelect = (template: Template) => {
    setSelectedTemplate(template);
  };

  const canGenerate = selectedTemplate && selectedFaceImage;

  const handleGenerate = async () => {
    if (!canGenerate || !selectedTemplate) return;

    setStatus('generating');
    setProgress(0);
    setError(null);
    setVideoUrl(null);
    
    // Track generation start - wrap in try-catch to prevent errors from breaking generation
    try {
      setStage('starting_generation', 0, 'Starting template video generation...');
    } catch (error) {
      console.error('❌ [TemplateToVideo] Progression tracking error (non-fatal):', error);
    }

    // Cancel any existing simulation
    if (cancelSimulationRef.current) {
      cancelSimulationRef.current();
    }

    if (!API_URL) {
      setError('API URL is not configured');
      setProgressionError('API URL is not configured');
      return;
    }

    // Use real API
    try {
      const request: TemplateToVideoRequest = {
        templateId: selectedTemplate.id,
        faceImage: selectedFaceImage!,
      };

      try {
        setStage('generating_video', 0, 'Generating video from template...');
      } catch (error) {
        console.error('❌ [TemplateToVideo] Progression tracking error (non-fatal):', error);
      }
      const response = await startTemplateToVideoJob(request);

      // Poll for status
      const pollStartTime = Date.now();
      const EIGHT_MINUTES_MS = 8 * 60 * 1000; // 8 minutes in milliseconds
      
      const pollInterval = setInterval(async () => {
        try {
          const elapsedTime = Date.now() - pollStartTime;
          const jobStatus = await pollTemplateJob(response.jobId);

          // Update progress from backend if available
          if (jobStatus.progress !== undefined) {
            setProgress(jobStatus.progress);
            try {
              updateStageProgress(jobStatus.progress);
            } catch (error) {
              console.error('❌ [TemplateToVideo] Progression progress error (non-fatal):', error);
            }
          }

          if (jobStatus.status === 'completed') {
            const finalVideoUrl = jobStatus.videoUrl;
            if (finalVideoUrl) {
              // Success! We have the video URL
              clearInterval(pollInterval);
              
              // Track final stages - wrap in try-catch to prevent errors from breaking completion
              try {
                setStage('storing_video_s3', 100, 'Video stored in S3');
                setTimeout(() => {
                  try {
                    setStage('storing_video_db', 100, 'Video record saved');
                    setTimeout(() => {
                      try {
                        setStage('completed', 100, 'Video ready!');
                      } catch (error) {
                        console.error('❌ [TemplateToVideo] Progression completion error (non-fatal):', error);
                      }
                    }, 500);
                  } catch (error) {
                    console.error('❌ [TemplateToVideo] Progression storage error (non-fatal):', error);
                  }
                }, 500);
              } catch (error) {
                console.error('❌ [TemplateToVideo] Progression tracking error (non-fatal):', error);
              }
              
              setVideoUrl(finalVideoUrl);
              setProgress(100);
              setStatus('completed');
            } else {
              // Status is completed but videoUrl is missing - continue polling indefinitely
              console.warn(`⚠️ [TemplateToVideo] Status is 'completed' but no videoUrl yet. This might be a race condition - continuing to poll...`);
              // Continue polling - DO NOT ERROR OUT
            }
          } else if (jobStatus.status === 'failed') {
            clearInterval(pollInterval);
            const errorMsg = jobStatus.error || 'Generation failed';
            setError(errorMsg);
            setStatus('failed');
            // Don't set progression error for video generation errors - they're separate concerns
            // Progression errors should only be for progression tracking issues
            console.error('❌ [TemplateToVideo] Generation failed');
          } else if (elapsedTime >= EIGHT_MINUTES_MS) {
            // DO NOT FAIL - just log and continue polling indefinitely
            // Backend might be slow but will eventually complete
            console.warn(`⏱️ [TemplateToVideo] 8 minutes elapsed but still processing - continuing to poll (will not timeout)`);
            // Continue polling - DO NOT ERROR OUT
          } else {
            // Still processing - continue polling
          }
        } catch (err) {
          console.error('❌ [TemplateToVideo] Error checking status:', err);
          
          // DO NOT FAIL - just log and continue polling indefinitely
          // Network issues are temporary, backend will eventually respond
          console.warn(`⚠️ [TemplateToVideo] Poll error - will retry, never giving up`);
          // Continue polling - DO NOT ERROR OUT
        }
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start generation');
      setStatus('failed');
    }
  };

  const handleReset = () => {
    setSelectedTemplate(null);
    setSelectedFaceImage(null);
    setFaceImagePreview(null);
    setStatus('idle');
    setProgress(0);
    setVideoUrl(null);
    setError(null);
    resetProgression();
    if (cancelSimulationRef.current) {
      cancelSimulationRef.current();
      cancelSimulationRef.current = null;
    }
  };

  return (
    <div className="min-h-full bg-bg px-4 sm:px-6 lg:px-8 py-12">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">Template to Video</h1>
          <p className="text-white/70 text-lg">
            Select a template and upload anyone to generate a video with them in it
          </p>
        </div>

        {/* Template Section */}
        <div className="mb-8">
          <label className="block text-white text-lg font-medium mb-3">Template</label>
          <div className="flex justify-center">
            <div className="relative w-full max-w-md">
              {selectedTemplate ? (
                <div className="relative border border-white/20 rounded-xl overflow-hidden bg-white/5">
                  <img
                    src={selectedTemplate.previewUrl}
                    alt={selectedTemplate.name}
                    className="w-full aspect-[9/16] object-cover"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="711"%3E%3Crect fill="%23333" width="400" height="711"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="20" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3ETemplate%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  <button
                    onClick={() => setSelectedTemplate(null)}
                    className="absolute top-3 right-3 w-8 h-8 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
                    aria-label="Remove template"
                  >
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <div
                  className="border-2 border-dashed border-white/30 rounded-xl p-12 cursor-pointer hover:border-white/50 transition-colors bg-white/5"
                  onClick={() => setIsTemplateModalOpen(true)}
                >
                  <div className="flex flex-col items-center">
                    <div className="w-24 h-24 mb-4 flex items-center justify-center">
                      <svg
                        className="w-full h-full text-white/50"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <p className="text-white text-sm font-medium">Click to select template</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Upload Face Image Section */}
        <div className="mb-8">
          <label className="block text-white text-lg font-medium mb-3">Upload Face Image</label>
          <div
            className={`border-2 border-dashed rounded-xl p-8 cursor-pointer transition-colors ${
              isDragging
                ? 'border-white bg-white/5'
                : selectedFaceImage
                ? 'border-white bg-white/5'
                : 'border-white/30 hover:border-white/50 bg-white/5'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg"
              onChange={handleFileInputChange}
              className="hidden"
            />
            {selectedFaceImage ? (
              <div className="flex flex-col items-center">
                <img
                  src={faceImagePreview || ''}
                  alt="Face preview"
                  className="w-full max-w-xs h-48 object-contain rounded-lg mb-4"
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedFaceImage(null);
                    setFaceImagePreview(null);
                  }}
                  className="text-white/70 hover:text-white text-sm"
                >
                  Remove
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 mb-4 flex items-center justify-center">
                  <svg
                    className="w-full h-full text-white/50"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-white text-sm font-medium mb-2">Click to upload</p>
                <p className="text-white/60 text-xs">PNG, JPG up to 10MB</p>
              </div>
            )}
          </div>
        </div>

        {/* Generate Button */}
        <div className="flex flex-col items-center mb-8">
          <button
            onClick={handleGenerate}
            disabled={!canGenerate || status === 'generating'}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-semibold px-12 py-4 rounded-full transition-colors text-lg"
          >
            {status === 'generating' ? 'Generating...' : 'Generate Video'}
          </button>
          <div className="mt-3 text-white/60 text-sm">
            <span>Est. {selectedTemplate?.estMinutes || '3-5'} min</span>
          </div>
        </div>

        {/* Generation Processing Modal */}
        {/* Show modal if generating OR if progression has started (not idle) */}
        <GenerationProcessingModal
          open={status === 'generating' || (progression.currentStage !== 'idle' && progression.currentStage !== 'completed' && progression.currentStage !== 'failed')}
          progress={progress}
          progression={progression}
        />

        {/* Error Message */}
        {error && (
          <div className="mb-8 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
            {error}
          </div>
        )}


        {/* Result Video */}
        {status === 'completed' && videoUrl && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-white mb-4">Result</h2>
            <div className="bg-white/5 border border-white/20 rounded-xl p-6 mb-4">
              <video
                src={videoUrl}
                controls
                className="w-full rounded-lg"
                onError={() => {
                  setError('Failed to load video. Using mock mode - video file may not exist.');
                }}
              >
                Your browser does not support the video tag.
              </video>
            </div>
            <div className="flex gap-4 justify-center">
              <a
                href={`${API_URL}/api/assets/download?video_url=${encodeURIComponent(videoUrl)}`}
                download
                className="bg-white text-black px-6 py-2 rounded-lg font-semibold hover:bg-gray-200 transition-colors"
              >
                Download
              </a>
              <button
                onClick={handleReset}
                className="bg-white/10 text-white px-6 py-2 rounded-lg font-semibold hover:bg-white/20 transition-colors"
              >
                Generate Another
              </button>
            </div>
          </div>
        )}

        {/* Template Picker Modal */}
        <TemplatePickerModal
          isOpen={isTemplateModalOpen}
          onClose={() => setIsTemplateModalOpen(false)}
          onSelect={handleTemplateSelect}
          selectedTemplateId={selectedTemplate?.id}
        />
      </div>
    </div>
  );
}

